import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Collapse, Grid, IconButton, Box, Typography, TableContainer, Link, Stack, Snackbar, Alert } from '@mui/material';
import { TableCell, TableBody, TableHead, TableRow, Table, TablePagination } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import NumberFormat from 'react-number-format';
import { getDepositsHistoryDetails, getDepositTransactions, selectDepositTransactionsState } from './sagas/pmDepositsSlice';
import { selectDepositsHistoryDetails, selectDepositsHistoryDetailsCurrentPage, selectDepositTransactions } from './sagas/pmDepositsSlice';
import ExportDataModal, { IFileExportDetails } from '../../components/ExportDataModal';
import ExportIcon from '../../icons/ExportIcon.svg';
import { DepositSearchTransactionResponseDto } from '@inosio/payments-sdk';
import TransactionDetails from '../transactions/components/TransactionDetails';
import { format, subDays } from 'date-fns';
import { useQuery } from '../transactions/Transactions';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../icons/Badrequesterror.svg';
import Error500 from '../../icons/505Error.svg';
import SearchableTableHeader from '../shared/SearchableTableHeader';
import { createRef, useEffect, useState } from 'react';
import { IsoDateRange } from '../shared/CalendarModal.V2';

const searchCategoryOptions = [
  { key: 'Type', value: 'Type' },
  { key: 'BankAccount', value: 'Bank Account' },
  { key: 'DepositID', value: 'Deposit / Debit ID' },
  { key: 'Amount', value: 'Amount' },
  { key: 'PaymentID', value: 'Payment ID' },
];

const useStyles = makeStyles({
  gridContainer: {
    background: '#FFFFFF',
    borderRadius: '24px',
  },
  dialog: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogbox: {
    borderRadius: '20',
    borderStyle: 'solid',
  },
});

export default function Deposits() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const query = useQuery();
  const transaction: DepositSearchTransactionResponseDto = {} as DepositSearchTransactionResponseDto;
  const defaultStartDate = format(subDays(new Date(), 90), 'yyyy-MM-dd');

  const [transactionuuidFromQuery, setTransactionuuidFromQuery] = useState(query.get('transactionuuid'));
  const [searchField, setsearchField] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState<string>(transactionuuidFromQuery || '');
  const [searchDateRange, setSearchDateRange] = useState<IsoDateRange | undefined>();
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(0);

  const [openDrop, setOpenDrop] = useState(false);
  const [selectedDepositId, setSelectedDepositId] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [depositAmountError, setDepositAmountEror] = useState('');
  const [openTransDetails, setOpenTransDetails] = useState(false);
  const [selectedTransRow, setSelectedTransRow] = useState(transaction);

  const columnsSelectRef = createRef<HTMLSelectElement>();
  const searchValueInputRef = createRef<HTMLInputElement>();

  const depositsHistoryData = useSelector(selectDepositsHistoryDetails);
  const depositsHistoryDataBook = useSelector(selectDepositsHistoryDetailsCurrentPage);
  const depositTransactions = useSelector(selectDepositTransactions);
  const depositTransactionsState = useSelector(selectDepositTransactionsState);

  const formatSearchAmount = (majorUnitAmount: string) => {
    if (majorUnitAmount.match(/[a-z]/i)) {
      setDepositAmountEror('Amount should be a valid number');
      setSnackBarOpen(true);
      return 0;
    }
    const formattedAmount = parseFloat(majorUnitAmount?.replace(/,/g, '')) * 100;
    if (majorUnitAmount === null || majorUnitAmount === '' || isNaN(formattedAmount)) {
      return 0;
    }
    return formattedAmount;
  };

  const handleSearches = (rangeSize: number, startIndex: number, dateRange?: IsoDateRange | undefined, searchField?: string, searchValue?: string, exportDetails?: IFileExportDetails) => {
    if (!searchValue) searchField = undefined;
    const despositSearchDto = {
      EndDate: dateRange?.[1] || undefined,
      StartDate: dateRange?.[0] || (!searchField? defaultStartDate: undefined),//we only want to put a default start date is there no search value
      RangeSize: rangeSize,
      StartIndex: startIndex,
      SearchField: searchField || undefined,
      SearchValue: searchValue || undefined,
      exportAs: exportDetails?.exportAs,
      fileName: exportDetails?.fileName,
    };

    if (searchValue) {
      if (searchField === 'Amount') {
        despositSearchDto.SearchValue = formatSearchAmount(searchValue).toString();
      } else if (isCaseInsensitiveMatch(searchValue, 'deposit')) {
        despositSearchDto.SearchValue = 'credit';
      } else if (isCaseInsensitiveMatch(searchValue, 'debit')) {
        despositSearchDto.SearchValue = 'debit';
      }
    }

    dispatch(getDepositsHistoryDetails(despositSearchDto));
  };
  const formatAmount = (cents: number) => +cents / 100;
  const toggleShowExportModal = () => setShowExportModal(!showExportModal);
  const handleCloseTransDetails = (event: object, reason: string) => setOpenTransDetails(false);

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!depositsHistoryDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  /**
   * Api Search needs to be invoked any time any of the following occurs:
   * - page is loaded initially
   * - pagination happens (and we don't have that data already)
   * - date/text search of some kind (table header)
   *
   */
  useEffect(() => {
    //if we have the trans query and it doesn't yet match the search value, then just return and let it re-render and search on next pass
    if (transactionuuidFromQuery && (transactionuuidFromQuery !== searchValue || !searchField)) return;

    handleSearches(rangeSize, recordStartIndex, searchDateRange, searchField, searchValue);
  }, [dispatch, rangeSize, recordStartIndex, searchDateRange, searchField, searchValue, transactionuuidFromQuery]);

  useEffect(() => {
    if (selectedDepositId) {
      if (depositTransactions.filter((e) => e.depositUUID === selectedDepositId).length === 0) {
        dispatch({
          type: getDepositTransactions.type,
          payload: {
            depositUuid: selectedDepositId,
          },
        });
      }
    }
  }, [dispatch, selectedDepositId]);

  useEffect(() => {
    if (transactionuuidFromQuery) {
      setsearchField(searchCategoryOptions[4].key);
      setSearchValue(transactionuuidFromQuery);
    }
  }, [transactionuuidFromQuery]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc( 100vh - 144px )', pb: '64px' }} className={classes.gridContainer} style={{ position: 'relative' }}>
        <React.Fragment>
          {
            <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
              <Alert onClose={handleAlertClose} severity="error">
                {depositAmountError}
              </Alert>
            </Snackbar>
          }
          <Box sx={{ display: 'flex', p: 1, pb: 2, justifyContent: 'space-between', alignItems: 'start', '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
            <SearchableTableHeader
              isDateOnly={true}
              initialSearchValue={searchValue}
              searchParam={searchField || searchCategoryOptions[2].key}
              searchValuePlaceholder="Type Here To Search"
              dropDownOptionValues={searchCategoryOptions}
              executeSearch={(searchField?: string | undefined, searchValue?: string | undefined, dateRange?: IsoDateRange | undefined) => {
                setsearchField(searchField);
                setSearchValue(searchValue || '');
                setSearchDateRange(dateRange);
                setRecordStartIndex(0); //reset start index
                setCurrentPage(0); //go back to page 1
                setTransactionuuidFromQuery(null);
                //we'll rely on our useEffect to invoke the search, as to not have the code in too many places
              }}
              searchValueInputRef={searchValueInputRef} //!WTF this do?
              columnsSelectRef={columnsSelectRef} //!WTF this do?
            />
            <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: '5px' }}>
              <IconButton aria-label="export" onClick={toggleShowExportModal}>
                <img src={ExportIcon} alt="export icon" width="22px" height="22px" />
              </IconButton>
            </Box>
          </Box>
          {String(depositTransactionsState.error.statusCode)[0] === '4' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" pb={3} mt={3}>
                  <img src={BadRequestIcon} alt="400" />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {depositTransactionsState.error.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {depositTransactionsState.error.message.length === 0 ? '' : depositTransactionsState.error.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : String(depositTransactionsState.error.statusCode)[0] === '5' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                  <img src={Error500} alt="500" style={{ height: '35vh' }} />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {depositTransactionsState.error.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {depositTransactionsState.error.message.length === 0 ? '' : depositTransactionsState.error.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : depositTransactionsState.isFetchingDeposits ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: '1100px',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : (
            <TableContainer style={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell id="subTitle">Type</TableCell>
                    <TableCell id="subTitle">Date</TableCell>
                    <TableCell id="subTitle">Bank Account</TableCell>
                    <TableCell id="subTitle">Deposit / Debit ID</TableCell>
                    <TableCell id="subTitle">Amount</TableCell>
                    <TableCell id="subTitle">View Details</TableCell>
                    {/* action */}
                  </TableRow>
                </TableHead>
                <TableBody style={{ left: '28px' }}>
                  {depositsHistoryDataBook?.[currentPage] &&
                    depositsHistoryDataBook?.[currentPage].Rows?.map((trans, index) => {
                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            key={trans.DepositUuid}
                            sx={{
                              '& .MuiTableCell-root': {
                                borderBottom: 'none',
                              },
                            }}
                          >
                            <TableCell id="content">{trans && trans.Type}</TableCell>
                            <TableCell id="content">
                              {trans && trans?.DepositDate ? `${trans?.DepositDate?.slice(5, 7)}/${trans && trans?.DepositDate?.slice(8, 10)}/${trans && trans?.DepositDate?.slice(0, 4)}` : ''}
                            </TableCell>
                            <TableCell id="content">{trans && trans.BankLast4}</TableCell>
                            <TableCell id="content">{trans.DepositUuid}</TableCell>
                            <TableCell id="content">
                              <NumberFormat
                                thousandsGroupStyle="thousand"
                                value={formatAmount(trans.Amount)}
                                prefix="$"
                                decimalSeparator="."
                                displayType="text"
                                type="text"
                                thousandSeparator={true}
                                allowNegative={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </TableCell>
                            <TableCell id="content">
                              {selectedDepositId === trans.DepositUuid && openDrop ? (
                                <IconButton
                                  aria-label="expand row"
                                  size="small"
                                  onClick={() => {
                                    setSelectedDepositId(trans.DepositUuid);
                                    setOpenDrop(false);
                                  }}
                                >
                                  <KeyboardArrowUpIcon />
                                </IconButton>
                              ) : (
                                <IconButton
                                  aria-label="expand row"
                                  size="small"
                                  onClick={() => {
                                    setSelectedDepositId(trans.DepositUuid);
                                    setOpenDrop(true);
                                  }}
                                >
                                  <KeyboardArrowDownIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                              <Collapse in={selectedDepositId === trans.DepositUuid && openDrop} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  <Typography component="h1" variant="h6" sx={{ fontWeight: 600, fontFamily: 'Work Sans, sans-serif', color: '#14142B', p: 1, flexGrow: 1 }}>
                                    {trans && trans.Type} Details
                                  </Typography>
                                  <Table size="small" aria-label="purchases">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell id="subTitle">Date</TableCell>
                                        <TableCell id="subTitle">Payment ID</TableCell>
                                        <TableCell id="subTitle">Name</TableCell>
                                        <TableCell id="subTitle">Property</TableCell>
                                        <TableCell id="subTitle">Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {depositTransactions.map((trans) => {
                                        if (trans.depositUUID === selectedDepositId) {
                                          return trans.Rows.map((transRow, i, row) => {
                                            return (
                                              <TableRow
                                                key={`${transRow.transactionUuid}${i}`}
                                                sx={{
                                                  '& .MuiTableCell-root':
                                                    i + 1 === row.length
                                                      ? {
                                                          borderBottom: 'none',
                                                        }
                                                      : {},
                                                }}
                                              >
                                                <TableCell id="content">{transRow && transRow?.transactionDate ? format(new Date(transRow?.transactionDate), 'P') : ''}</TableCell>
                                                <TableCell id="content">
                                                  <Link
                                                    href="#"
                                                    underline="hover"
                                                    color="inherit"
                                                    onClick={() => {
                                                      setOpenTransDetails(true);
                                                      setSelectedTransRow(transRow);
                                                    }}
                                                  >
                                                    {transRow.transactionUuid}
                                                  </Link>
                                                </TableCell>
                                                <TableCell id="content">{`${transRow.payerFirstName} ${transRow.payerLastName}`}</TableCell>
                                                <TableCell id="content">{transRow.PropertyName}</TableCell>
                                                <TableCell id="content">
                                                  <NumberFormat
                                                    thousandsGroupStyle="thousand"
                                                    value={formatAmount(transRow.transactionAmount)}
                                                    prefix="$"
                                                    decimalSeparator="."
                                                    displayType="text"
                                                    type="text"
                                                    thousandSeparator={true}
                                                    allowNegative={true}
                                                    decimalScale={2}
                                                    fixedDecimalScale={true}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            );
                                          });
                                        }
                                        return null;
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[50, 100, 200]}
            count={depositsHistoryData.TotalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ position: 'absolute', bottom: 10, right: 10 }}
          />
          {selectedTransRow && Object.keys(selectedTransRow).length !== 0 ? (
            <TransactionDetails
              open={openTransDetails}
              closeCallback={handleCloseTransDetails}
              deviceWidth={window.innerWidth}
              paymentID={selectedTransRow.transactionUuid}
              transactionDate={selectedTransRow?.transactionDate}
              transactionDateShort={format(new Date(selectedTransRow?.transactionDate), 'MM/dd/yy')}
              refundReceipt={false}
              hideDepositBtn={true}
            />
          ) : (
            <></>
          )}
          <ExportDataModal
            name={'Deposit'}
            visible={showExportModal}
            onExport={(fileType, fileName) => handleSearches(0, 0, searchDateRange, searchField, searchValue, { exportAs: fileType, fileName })}
            toggleShowExportModal={toggleShowExportModal}
          />
        </React.Fragment>
      </Card>
    </Grid>
  );
}

// checks if the input string matches the comapreTo string similar to how a sql like '%%' would work
function isCaseInsensitiveMatch(input: string, compareTo: string) {
  if ((input + '').length > 0) {
    return compareTo.toUpperCase().match(`[A-Z]*${input.toUpperCase()}[A-Z]*`);
  }

  return false;
}
